import { useState, useMemo, useEffect } from 'react'
import {
  Box, Typography, TextField, InputAdornment, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, MenuItem, Stack, Skeleton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useApi } from '../../hooks/useApi'
import ExportMenu from '../../components/common/ExportMenu'

// FAR-44 Item Data screen. Read-only view of master data (mastered in SAP,
// loaded via the master-data ingest). All four plants are loaded; the Plant
// filter defaults to Faribault (1002). Other plants are round-2 scope.

const DEFAULT_PLANT = '1002'

// "—" for unset numeric/DOS values so blank is visually distinct from a real 0.
const DASH = '—'

// A blank coverage profile means the DOS bounds are "not assigned", which the
// spec requires we show as "—" rather than 0 (FAR-44 §6).
function dosDisplay(hasCoverageProfile, value) {
  return hasCoverageProfile ? value : DASH
}

// Column order per FAR-44 §4 (exact left-to-right).
const COLUMNS = [
  { id: 'materialNumber', label: 'Item #', numeric: false },
  { id: 'description', label: 'Description', numeric: false },
  { id: 'canSize', label: 'Can Size', numeric: false },
  { id: 'cansPerCase', label: 'Cans per Case', numeric: true },
  { id: 'ecFactor', label: 'EC Factor', numeric: true },
  { id: 'dosMin', label: 'Min DOS', numeric: true, dos: true },
  { id: 'dosTarget', label: 'Target DOS', numeric: true, dos: true },
  { id: 'dosMax', label: 'Max DOS', numeric: true, dos: true },
  { id: 'plant', label: 'Plant', numeric: false },
  { id: 'plantDescription', label: 'Plant Description', numeric: false },
  { id: 'baseUom', label: 'Base Unit of Measure', numeric: false },
  { id: 'lotSizeIndicator', label: 'Lot Size Indicator', numeric: false },
  { id: 'lotSizeDescription', label: 'Lot Size Description', numeric: false },
  { id: 'roundingValue', label: 'Rounding Value', numeric: true },
]

export default function ItemData() {
  const { get, loading, error } = useApi()
  const [rows, setRows] = useState([])
  const [itemSearch, setItemSearch] = useState('')
  const [descSearch, setDescSearch] = useState('')
  const [canSizeFilter, setCanSizeFilter] = useState('')
  const [plantFilter, setPlantFilter] = useState(DEFAULT_PLANT)
  const [orderBy, setOrderBy] = useState('materialNumber')
  const [order, setOrder] = useState('asc')

  useEffect(() => {
    let active = true
    // Load all plants once; filter client-side for snappy incremental search.
    get('/items')
      .then((res) => {
        if (active) setRows(res?.items || [])
      })
      .catch(() => {
        /* error surfaced via useApi().error */
      })
    return () => {
      active = false
    }
  }, [get])

  const canSizeOptions = useMemo(
    () => [...new Set(rows.map((r) => r.canSize).filter(Boolean))].sort(),
    [rows],
  )
  const plantOptions = useMemo(
    () => [...new Set(rows.map((r) => r.plant).filter(Boolean))].sort(),
    [rows],
  )

  const filtered = useMemo(() => {
    const itemQ = itemSearch.trim().toLowerCase()
    const descQ = descSearch.trim().toLowerCase()
    // AND logic across all filters (FAR-44 §5).
    let out = rows.filter((r) => {
      if (plantFilter && r.plant !== plantFilter) return false
      if (canSizeFilter && r.canSize !== canSizeFilter) return false
      if (itemQ && !String(r.materialNumber).toLowerCase().includes(itemQ)) return false
      if (descQ && !String(r.description).toLowerCase().includes(descQ)) return false
      return true
    })
    out = [...out].sort((a, b) => {
      const av = a[orderBy]
      const bv = b[orderBy]
      let cmp
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''))
      return order === 'asc' ? cmp : -cmp
    })
    return out
  }, [rows, itemSearch, descSearch, canSizeFilter, plantFilter, orderBy, order])

  const handleSort = (id) => {
    if (orderBy === id) setOrder(order === 'asc' ? 'desc' : 'asc')
    else {
      setOrderBy(id)
      setOrder('asc')
    }
  }

  const renderCell = (row, col) => {
    if (col.dos) return dosDisplay(row.hasCoverageProfile, row[col.id])
    const v = row[col.id]
    if (col.numeric) return v || v === 0 ? v : DASH
    return v || ''
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5">Item Data</Typography>
        <ExportMenu />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Read-only master data. Mastered in SAP; corrections happen at the SAP
        source and flow through on the next load.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} flexWrap="wrap">
        <TextField
          size="small" label="Item #" placeholder="exact or partial"
          value={itemSearch} onChange={(e) => setItemSearch(e.target.value)}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small" placeholder="Search description"
          value={descSearch} onChange={(e) => setDescSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select size="small" label="Can Size" value={canSizeFilter}
          onChange={(e) => setCanSizeFilter(e.target.value)} sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All can sizes</MenuItem>
          {canSizeOptions.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <TextField
          select size="small" label="Plant" value={plantFilter}
          onChange={(e) => setPlantFilter(e.target.value)} sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All plants</MenuItem>
          {plantOptions.map((p) => (
            <MenuItem key={p} value={p}>{p}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {loading ? 'Loading…' : `${filtered.length} item${filtered.length === 1 ? '' : 's'}`}
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              rows.length === 0 &&
              [...Array(8)].map((_, i) => (
                <TableRow key={i}>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.id}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No items match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((row) => (
              <TableRow key={`${row.plant}-${row.materialNumber}`} hover>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} align={col.numeric ? 'right' : 'left'}>
                    {renderCell(row, col)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
